import React, { useState, useEffect, useRef } from 'react';
import {
  Users, FileText, Upload, Download, CheckCircle, Clock,
  XCircle, AlertTriangle, Search, Plus, Eye, X, ChevronLeft, Edit2, Save
} from 'lucide-react';
import RichTextEditor from '../../shared/RichTextEditor';
import HRDocumentService, { HRDocument, DEFAULT_HR_FORM_TEMPLATES } from '../../../services/HRDocumentService';
import VoiceInputButton from '../../VoiceInputButton';
import FormTemplateService, { FormTemplateDefinition } from '../../../services/FormTemplateService';
import { CLINICAL_FORM_TEMPLATES } from '../../../data/clinicalFormTemplates';
import { storage } from '../../../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import TeamMemberService, { TeamMember } from '../../../services/TeamMemberService';
import {
  HR_DOCUMENT_LABELS,
  isCredentialDocument,
  isComplianceDocument
} from '../../../types/roles';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../components/Toast';

interface HRTabProps {
  providerId: string;
  providerName: string;
}

type ViewMode = 'overview' | 'employees' | 'documents' | 'assign' | 'employee-detail' | 'live-docs' | 'my-docs';
type FilterStatus = 'all' | 'pending' | 'submitted' | 'approved' | 'rejected';
type FilterType = 'all' | 'credentials' | 'compliance';

const hrService = HRDocumentService.getInstance();
const teamService = TeamMemberService.getInstance();

const HRTab: React.FC<HRTabProps> = ({ providerId, providerName }) => {
  const { user, roles } = useAuth();
  const toast = useToast();
  const isHRManager = roles.some(r => ['system_admin', 'clinical_director', 'administrative_staff'].includes(r));
  const [myTeamMemberId, setMyTeamMemberId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(isHRManager ? 'overview' : 'my-docs');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [documents, setDocuments] = useState<HRDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [filterType, setFilterType] = useState<FilterType>('all');

  // Assign modal state
  const [assignEmployeeId, setAssignEmployeeId] = useState('');
  const [assignTemplateId, setAssignTemplateId] = useState('');
  const [assignPriority, setAssignPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [assignDueDate, setAssignDueDate] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignSuccess, setAssignSuccess] = useState('');

  // Detail modal state
  const [selectedDoc, setSelectedDoc] = useState<HRDocument | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [actionNotes, setActionNotes] = useState('');

  // Employee detail state
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  // Live editor state
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [editorDocId, setEditorDocId] = useState<string | null>(null);
  const [editorTitle, setEditorTitle] = useState('');
  const [savingContent, setSavingContent] = useState(false);
  const [showNewDocModal, setShowNewDocModal] = useState(false);
  const [newDocTitle, setNewDocTitle] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Installed HR templates from Document Library
  const [installedHRTemplates, setInstalledHRTemplates] = useState<FormTemplateDefinition[]>([]);

  // Form fill/sign state
  const [showFormFillModal, setShowFormFillModal] = useState(false);
  const [formFillDoc, setFormFillDoc] = useState<HRDocument | null>(null);
  const [formFillTemplate, setFormFillTemplate] = useState<FormTemplateDefinition | null>(null);
  const [formResponses, setFormResponses] = useState<Record<string, any>>({});
  const [signatureName, setSignatureName] = useState('');

  // ── Load data ─────────────────────────────────────────────────────────

  useEffect(() => {
    loadTeamMembers();
    const unsub = hrService.subscribeToDocuments(providerId, (docs) => {
      setDocuments(docs);
      setLoading(false);
    });
    return () => unsub();
  }, [providerId]);

  // Load installed HR templates from Document Library
  useEffect(() => {
    FormTemplateService.getInstance().getTemplatesByCategory('hr').then(templates => {
      setInstalledHRTemplates(templates);
    }).catch(() => {});
  }, []);

  const loadTeamMembers = () => {
    try {
      teamService.startListening(providerId);
      const members = teamService.getAllTeamMembers();
      setTeamMembers(members.filter((m: TeamMember) => m.status === 'active'));
      // Resolve current user's team member ID
      const me = members.find((m: TeamMember) => m.authUid === user?.uid);
      if (me) setMyTeamMemberId(me.id);
      // Re-check when team members update
      const onUpdate = () => {
        const updated = teamService.getAllTeamMembers();
        setTeamMembers(updated.filter((m: TeamMember) => m.status === 'active'));
        if (!myTeamMemberId) {
          const meNow = updated.find((m: TeamMember) => m.authUid === user?.uid);
          if (meNow) setMyTeamMemberId(meNow.id);
        }
      };
      teamService.subscribe(onUpdate);
      return () => teamService.unsubscribe(onUpdate);
    } catch (err) {
      console.error('Error loading team members:', err);
    }
  };

  // ── Stats ─────────────────────────────────────────────────────────────

  // Stats use the role-appropriate doc set (computed after myDocuments is available)
  const statsDocs = !isHRManager && myTeamMemberId
    ? documents.filter(d => d.employeeId === myTeamMemberId)
    : documents;
  const stats = {
    total: statsDocs.length,
    completed: statsDocs.filter(d => d.status === 'approved' || d.status === 'submitted').length,
    pending: statsDocs.filter(d => d.status === 'pending' || d.status === 'rejected').length,
    submitted: statsDocs.filter(d => d.status === 'submitted').length,
    percentage: statsDocs.length > 0
      ? Math.round((statsDocs.filter(d => d.status === 'approved' || d.status === 'submitted').length / statsDocs.length) * 100)
      : 0,
  };

  // ── My documents (for non-HR-manager roles) ─────────────────────────
  const myDocuments = !isHRManager && myTeamMemberId
    ? documents.filter(d => d.employeeId === myTeamMemberId)
    : documents;

  // ── Filters ───────────────────────────────────────────────────────────

  const filteredDocuments = (isHRManager ? documents : myDocuments).filter(doc => {
    const matchesSearch = !searchTerm ||
      doc.formName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.employeeName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || doc.status === filterStatus;
    const matchesType = filterType === 'all' ||
      (filterType === 'credentials' && isCredentialDocument(doc.documentType)) ||
      (filterType === 'compliance' && isComplianceDocument(doc.documentType));
    return matchesSearch && matchesStatus && matchesType;
  });

  // ── Assign handler ────────────────────────────────────────────────────

  const handleAssign = async () => {
    if (!assignEmployeeId || !assignTemplateId) return;
    setAssigning(true);
    setAssignSuccess('');
    try {
      const member = teamMembers.find(m => m.id === assignEmployeeId);
      // Check if this is an installed template (prefixed with 'installed:')
      const isInstalled = assignTemplateId.startsWith('installed:');
      const installedId = isInstalled ? assignTemplateId.replace('installed:', '') : null;
      const installedTemplate = installedId ? installedHRTemplates.find(t => t.id === installedId) : null;

      if (isInstalled && installedTemplate) {
        // Assign installed template — create HR doc with templateId
        const { addDoc, collection: firestoreCollection } = await import('firebase/firestore');
        const { db } = await import('../../../config/firebase');
        await addDoc(firestoreCollection(db, 'hr_documents'), {
          employeeId: assignEmployeeId,
          employeeName: member?.name || 'Unknown',
          documentType: 'compliance',
          formName: installedTemplate.name,
          status: 'pending',
          assignedDate: new Date().toISOString(),
          assignedBy: providerId,
          practiceOwnerId: providerId,
          priority: assignPriority,
          ...(assignDueDate ? { dueDate: assignDueDate } : {}),
          templateId: installedId,
        });
        setAssignSuccess(`Assigned "${installedTemplate.name}" to ${member?.name}`);
      } else {
        await hrService.assignDocumentToEmployee(
          assignEmployeeId,
          member?.name || 'Unknown',
          assignTemplateId,
          providerId,
          providerId,
          assignPriority,
          assignDueDate || undefined
        );
        const template = DEFAULT_HR_FORM_TEMPLATES.find(t => t.id === assignTemplateId);
        setAssignSuccess(`Assigned "${template?.formName}" to ${member?.name}`);
      }
      setAssignTemplateId('');
      setAssignDueDate('');
    } catch (err: any) {
      toast.error('Error', 'Error assigning document: ' + err.message);
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignAllRequired = async () => {
    if (!assignEmployeeId) return;
    setAssigning(true);
    setAssignSuccess('');
    try {
      const member = teamMembers.find(m => m.id === assignEmployeeId);
      const count = await hrService.assignStandardFormsToNewEmployee(
        assignEmployeeId,
        member?.name || 'Unknown',
        providerId,
        providerId
      );
      setAssignSuccess(`Assigned ${count} required forms to ${member?.name}`);
    } catch (err: any) {
      toast.error('Error', 'Error: ' + err.message);
    } finally {
      setAssigning(false);
    }
  };

  // ── Open Fill & Sign sidebar directly for a document ──────────────────
  const openFillAndSign = async (doc: HRDocument) => {
    let tmpl: FormTemplateDefinition | null = null;
    // 1. Check installed templates by templateId
    if (doc.templateId) {
      tmpl = installedHRTemplates.find(t => t.id === doc.templateId) || null;
      if (!tmpl) {
        const fetched = await FormTemplateService.getInstance().getTemplatesByCategory('hr');
        tmpl = fetched.find(t => t.id === doc.templateId) || null;
      }
    }
    // 2. Fallback: look up by name in static CLINICAL_FORM_TEMPLATES
    if (!tmpl) {
      const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, ' ').replace(/\s+/g, ' ').trim();
      const docWords = normalize(doc.formName);
      const staticTmpl = CLINICAL_FORM_TEMPLATES.find(t => t.name === doc.formName)
        || CLINICAL_FORM_TEMPLATES.find(t => normalize(t.name) === docWords)
        // Check if one name contains the other (e.g., "Employment Contract" in "Employment Contract")
        || CLINICAL_FORM_TEMPLATES.find(t => t.category === 'hr' && (
          normalize(t.name).includes(docWords) || docWords.includes(normalize(t.name))
        ))
        // Fuzzy: match on shared significant words
        || CLINICAL_FORM_TEMPLATES.find(t => {
          if (t.category !== 'hr') return false;
          const tWords = normalize(t.name).split(' ');
          const dWords = docWords.split(' ');
          const matches = tWords.filter(w => dWords.includes(w) && w.length > 2);
          return matches.length >= 1 && (matches.some(w => w.length > 5));
        });
      if (staticTmpl) {
        tmpl = {
          id: `static_${staticTmpl.name.replace(/\s+/g, '_').toLowerCase()}`,
          name: staticTmpl.name,
          category: staticTmpl.category,
          description: staticTmpl.description,
          required: false,
          elements: staticTmpl.elements,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: 1,
        };
      }
    }
    if (!tmpl) {
      // No template found — fall back to detail modal
      setSelectedDoc(doc);
      setShowDetailModal(true);
      setActionNotes('');
      setSignatureName('');
      return;
    }
    setFormFillTemplate(tmpl);
    setFormFillDoc(doc);
    setFormResponses(doc.formResponses || {});
    setSignatureName('');
    setShowFormFillModal(true);
  };

  // ── File upload for a document ────────────────────────────────────────

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, docId: string) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const storagePath = `hr/${providerId}/${docId}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      await hrService.markDocumentAsSubmitted(docId, downloadUrl, storagePath);
      setSelectedDoc(prev => prev ? { ...prev, status: 'submitted', fileUrl: downloadUrl } : null);
    } catch (err: any) {
      toast.error('Upload Failed', 'Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  // ── Approve / Reject ──────────────────────────────────────────────────

  const handleApprove = async (docId: string) => {
    try {
      await hrService.approveDocument(docId, providerId, actionNotes || undefined);
      setShowDetailModal(false);
      setActionNotes('');
    } catch (err: any) {
      toast.error('Error', 'Error: ' + err.message);
    }
  };

  const handleReject = async (docId: string) => {
    if (!actionNotes.trim()) { toast.warning('Missing Note', 'Please add a note explaining the rejection.'); return; }
    try {
      await hrService.rejectDocument(docId, providerId, actionNotes);
      setShowDetailModal(false);
      setActionNotes('');
    } catch (err: any) {
      toast.error('Error', 'Error: ' + err.message);
    }
  };

  // ── Live Document Handlers ───────────────────────────────────────────

  const openEditor = (doc: HRDocument) => {
    setEditorDocId(doc.id);
    setEditorTitle(doc.formName);
    setEditorContent(doc.liveContent || '');
    setShowEditorModal(true);
  };

  const handleSaveContent = async () => {
    if (!editorDocId) return;
    setSavingContent(true);
    try {
      await hrService.updateLiveContent(editorDocId, editorContent, providerId);
      setSavingContent(false);
    } catch (err: any) {
      toast.error('Save Failed', 'Save failed: ' + err.message);
      setSavingContent(false);
    }
  };

  const handleCreateLiveDoc = async () => {
    if (!newDocTitle.trim()) return;
    try {
      const id = await hrService.createLiveDocument(
        providerId, newDocTitle.trim(), providerId, providerName
      );
      setNewDocTitle('');
      setShowNewDocModal(false);
      // Open the new document in the editor
      setEditorDocId(id);
      setEditorTitle(newDocTitle.trim());
      setEditorContent('');
      setShowEditorModal(true);
    } catch (err: any) {
      toast.error('Error', 'Error creating document: ' + err.message);
    }
  };

  const liveDocuments = documents.filter(d => d.liveContent !== undefined && d.liveContent !== null);

  // ── Helpers ───────────────────────────────────────────────────────────

  const statusBadge = (status: HRDocument['status']) => {
    const colors: Record<string, string> = {
      approved: 'background-color: #d1fae5; color: #065f46',
      submitted: 'background-color: #dbeafe; color: #1e40af',
      pending: 'background-color: #fef3c7; color: #92400e',
      rejected: 'background-color: #fee2e2; color: #991b1b',
      expired: 'background-color: #f3f4f6; color: #374151',
    };
    return colors[status] || colors.pending;
  };

  const StatusIcon: React.FC<{ status: HRDocument['status'] }> = ({ status }) => {
    switch (status) {
      case 'approved': return <CheckCircle size={14} />;
      case 'submitted': return <Upload size={14} />;
      case 'pending': return <Clock size={14} />;
      case 'rejected': return <XCircle size={14} />;
      case 'expired': return <AlertTriangle size={14} />;
      default: return <Clock size={14} />;
    }
  };

  // Employee docs for detail view
  const selectedEmployeeDocs = documents.filter(d => d.employeeId === selectedEmployeeId);
  const selectedEmployee = teamMembers.find(m => m.id === selectedEmployeeId);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <div style={{ width: '32px', height: '32px', border: '3px solid #87CEEB', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#f9fafb', margin: 0 }}>
            {isHRManager ? 'HR Management' : 'My HR Documents'}
          </h2>
          <p style={{ color: '#94a3b8', marginTop: '4px', fontSize: '14px' }}>
            {isHRManager ? 'Employee documents and compliance tracking' : 'View and complete your assigned documents'}
          </p>
        </div>
        {isHRManager && (
          <button
            onClick={() => setViewMode('assign')}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px',
              backgroundColor: '#226F9E', color: '#FFFFFF', border: 'none', borderRadius: '8px',
              cursor: 'pointer', fontSize: '14px', fontWeight: 600,
            }}
          >
            <Plus size={18} /> Assign Documents
          </button>
        )}
      </div>

      {/* View Mode Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid #E5E7EB' }}>
        {(isHRManager ? [
          { id: 'overview', label: 'Overview', icon: FileText },
          { id: 'my-docs', label: 'My Documents', icon: Eye },
          { id: 'employees', label: 'Employees', icon: Users },
          { id: 'documents', label: 'All Documents', icon: FileText },
          { id: 'live-docs', label: 'Live Documents', icon: Edit2 },
        ] as const : [
          { id: 'my-docs', label: 'My Documents', icon: Eye },
        ] as const).map((tab) => {
          const Icon = tab.icon;
          const active = viewMode === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setViewMode(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '12px 16px',
                border: 'none', borderBottom: `2px solid ${active ? '#226F9E' : 'transparent'}`,
                backgroundColor: 'transparent', cursor: 'pointer',
                color: active ? '#226F9E' : '#6B7280', fontWeight: active ? 600 : 400, fontSize: '14px',
              }}
            >
              <Icon size={16} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── MY DOCUMENTS ─────────────────────────────────────────────── */}
      {viewMode === 'my-docs' && (
        (() => {
          const myDocs = documents.filter(d => d.employeeId === myTeamMemberId);
          const pendingDocs = myDocs.filter(d => d.status === 'pending' || d.status === 'rejected');
          const completedDocs = myDocs.filter(d => d.status === 'submitted' || d.status === 'approved');
          return (
            <>
              {myDocs.length === 0 ? (
                <div style={{
                  textAlign: 'center', padding: '48px 24px', backgroundColor: '#F9FAFB',
                  borderRadius: '12px', border: '1px solid #E5E7EB',
                }}>
                  <CheckCircle size={40} color="#059669" style={{ marginBottom: '12px' }} />
                  <p style={{ fontSize: '16px', fontWeight: 600, color: '#374151', margin: '0 0 4px 0' }}>
                    No documents assigned
                  </p>
                  <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                    You're all set! Documents assigned to you will appear here.
                  </p>
                </div>
              ) : (
                <>
                  {/* Action Required */}
                  {pendingDocs.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#0A2540', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <AlertTriangle size={18} color="#D97706" />
                        Action Required ({pendingDocs.length})
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {pendingDocs.map(doc => {
                          const tmpl = DEFAULT_HR_FORM_TEMPLATES.find(t => t.formName === doc.formName);
                          return (
                            <div
                              key={doc.id}
                              onClick={() => openFillAndSign(doc)}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '16px 20px', backgroundColor: '#FFFBEB', borderRadius: '10px',
                                border: '1px solid #FDE68A', cursor: 'pointer', transition: 'all 0.2s',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#FEF3C7'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FFFBEB'; e.currentTarget.style.transform = 'translateY(0)'; }}
                            >
                              <div>
                                <p style={{ fontSize: '15px', fontWeight: 600, color: '#0A2540', margin: '0 0 4px 0' }}>{doc.formName}</p>
                                {tmpl && <p style={{ fontSize: '13px', color: '#78716C', margin: 0 }}>{tmpl.description}</p>}
                                {doc.dueDate && (
                                  <p style={{ fontSize: '12px', color: new Date(doc.dueDate) < new Date() ? '#DC2626' : '#92400E', margin: '4px 0 0 0', fontWeight: 500 }}>
                                    Due: {new Date(doc.dueDate).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{
                                  padding: '4px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 500,
                                  backgroundColor: doc.status === 'rejected' ? '#FEE2E2' : '#FEF3C7',
                                  color: doc.status === 'rejected' ? '#991B1B' : '#92400E',
                                }}>
                                  {doc.status === 'rejected' ? 'Needs Revision' : 'Pending'}
                                </span>
                                <ChevronLeft size={16} color="#9CA3AF" style={{ transform: 'rotate(180deg)' }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Completed */}
                  {completedDocs.length > 0 && (
                    <div>
                      <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#0A2540', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CheckCircle size={18} color="#059669" />
                        Completed ({completedDocs.length})
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {completedDocs.map(doc => (
                          <div
                            key={doc.id}
                            onClick={() => { setSelectedDoc(doc); setShowDetailModal(true); setActionNotes(''); }}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '14px 20px', backgroundColor: '#F0FDF4', borderRadius: '10px',
                              border: '1px solid #BBF7D0', cursor: 'pointer',
                            }}
                          >
                            <div>
                              <p style={{ fontSize: '14px', fontWeight: 500, color: '#0A2540', margin: 0 }}>{doc.formName}</p>
                              {doc.signedAt && (
                                <p style={{ fontSize: '12px', color: '#059669', margin: '4px 0 0 0' }}>
                                  Signed by {doc.signedByName} on {new Date(doc.signedAt).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                            <span style={{
                              padding: '4px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 500,
                              backgroundColor: doc.status === 'approved' ? '#D1FAE5' : '#DBEAFE',
                              color: doc.status === 'approved' ? '#065F46' : '#1E40AF',
                            }}>
                              {doc.status === 'approved' ? 'Approved' : 'Submitted'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          );
        })()
      )}

      {/* ── OVERVIEW ──────────────────────────────────────────────────── */}
      {viewMode === 'overview' && (
        <>
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
            {[
              { label: 'Total Documents', value: stats.total, color: '#226F9E' },
              { label: 'Completed', value: stats.completed, color: '#059669' },
              { label: 'Pending', value: stats.pending, color: '#D97706' },
              { label: 'Completion Rate', value: stats.percentage + '%', color: '#226F9E' },
            ].map(s => (
              <div key={s.label} style={{ backgroundColor: '#FFFFFF', padding: '20px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>{s.label}</p>
                <p style={{ fontSize: '28px', fontWeight: 700, color: s.color, margin: '8px 0 0 0' }}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Awaiting Review */}
          {documents.filter(d => d.status === 'submitted').length > 0 && (
            <div style={{ backgroundColor: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#9A3412', margin: '0 0 12px 0' }}>
                Awaiting Your Review ({documents.filter(d => d.status === 'submitted').length})
              </h4>
              {documents.filter(d => d.status === 'submitted').map(doc => (
                <div key={doc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #FED7AA' }}>
                  <div>
                    <span style={{ fontWeight: 500, color: '#0A2540' }}>{doc.formName}</span>
                    <span style={{ color: '#6B7280', marginLeft: '8px', fontSize: '13px' }}>— {doc.employeeName}</span>
                  </div>
                  <button
                    onClick={() => { setSelectedDoc(doc); setShowDetailModal(true); }}
                    style={{ padding: '4px 12px', backgroundColor: '#226F9E', color: '#FFF', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                  >
                    Review
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Recent Activity */}
          <div style={{ backgroundColor: '#FFFFFF', padding: '20px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#0A2540', margin: '0 0 16px 0' }}>Recent Activity</h3>
            {documents.length === 0 && <p style={{ color: '#6B7280', fontSize: '14px' }}>No HR documents yet. Use "Assign Documents" to get started.</p>}
            {documents.slice(0, 8).map(doc => (
              <div key={doc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ padding: '6px', borderRadius: '6px', backgroundColor: doc.status === 'approved' ? '#D1FAE5' : doc.status === 'submitted' ? '#DBEAFE' : '#FEF3C7' }}>
                    <StatusIcon status={doc.status} />
                  </div>
                  <div>
                    <p style={{ fontWeight: 500, color: '#0A2540', margin: 0, fontSize: '14px' }}>{doc.formName}</p>
                    <p style={{ color: '#6B7280', margin: 0, fontSize: '12px' }}>{doc.employeeName}</p>
                  </div>
                </div>
                <span
                  style={{
                    padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 500,
                    ...(() => {
                      const c: Record<string, { backgroundColor: string; color: string }> = {
                        approved: { backgroundColor: '#D1FAE5', color: '#065F46' },
                        submitted: { backgroundColor: '#DBEAFE', color: '#1E40AF' },
                        pending: { backgroundColor: '#FEF3C7', color: '#92400E' },
                        rejected: { backgroundColor: '#FEE2E2', color: '#991B1B' },
                      };
                      return c[doc.status] || c.pending;
                    })()
                  }}
                >
                  {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── EMPLOYEES ─────────────────────────────────────────────────── */}
      {viewMode === 'employees' && (
        <>
          <div style={{ marginBottom: '16px', position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
            <input
              type="text" placeholder="Search employees..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
            />
          </div>

          <div style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB' }}>
                  {['Employee', 'Role', 'Status', 'Documents', 'Completion', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teamMembers
                  .filter(m => !searchTerm || m.name.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map(member => {
                    const empDocs = documents.filter(d => d.employeeId === member.id);
                    const approved = empDocs.filter(d => d.status === 'approved').length;
                    const pct = empDocs.length > 0 ? Math.round((approved / empDocs.length) * 100) : 0;
                    return (
                      <tr key={member.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: 500, color: '#0A2540', fontSize: '14px' }}>{member.name}</div>
                          <div style={{ fontSize: '12px', color: '#6B7280' }}>{member.email}</div>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#374151' }}>{member.role}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            padding: '2px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 500,
                            backgroundColor: member.status === 'active' ? '#D1FAE5' : '#F3F4F6',
                            color: member.status === 'active' ? '#065F46' : '#6B7280',
                          }}>
                            {member.status || 'active'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#374151' }}>
                          {approved}/{empDocs.length}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '60px', height: '6px', backgroundColor: '#E5E7EB', borderRadius: '3px', overflow: 'hidden' }}>
                              <div style={{ width: `${pct}%`, height: '100%', backgroundColor: '#059669', borderRadius: '3px' }} />
                            </div>
                            <span style={{ fontSize: '12px', color: '#6B7280' }}>{pct}%</span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <button
                            onClick={() => { setSelectedEmployeeId(member.id); setViewMode('employee-detail'); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 12px',
                              backgroundColor: 'transparent', color: '#226F9E', border: '1px solid #226F9E',
                              borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
                            }}
                          >
                            <Eye size={14} /> View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                {teamMembers.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#6B7280' }}>No team members found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── EMPLOYEE DETAIL ───────────────────────────────────────────── */}
      {viewMode === 'employee-detail' && selectedEmployee && (
        <>
          <button
            onClick={() => setViewMode('employees')}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 0', backgroundColor: 'transparent', border: 'none', color: '#226F9E', cursor: 'pointer', fontSize: '14px', marginBottom: '16px' }}
          >
            <ChevronLeft size={16} /> Back to Employees
          </button>

          <div style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', border: '1px solid #E5E7EB', padding: '20px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#0A2540', margin: 0 }}>{selectedEmployee.name}</h3>
                <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0 0' }}>{selectedEmployee.role} — {selectedEmployee.email}</p>
              </div>
              <button
                onClick={() => { setAssignEmployeeId(selectedEmployee.id); setViewMode('assign'); }}
                style={{
                  padding: '8px 16px', backgroundColor: '#226F9E', color: '#FFF',
                  border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                }}
              >
                <Plus size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Assign Document
              </button>
            </div>
          </div>

          {selectedEmployeeDocs.length === 0 ? (
            <div style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', border: '1px solid #E5E7EB', padding: '32px', textAlign: 'center' }}>
              <p style={{ color: '#6B7280', fontSize: '14px' }}>No documents assigned yet.</p>
            </div>
          ) : (
            <div style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F9FAFB' }}>
                    {['Document', 'Type', 'Status', 'Priority', 'Due Date', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selectedEmployeeDocs.map(doc => (
                    <tr key={doc.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                      <td style={{ padding: '10px 16px', fontSize: '14px', fontWeight: 500, color: '#0A2540' }}>{doc.formName}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
                          backgroundColor: isCredentialDocument(doc.documentType) ? '#EDE9FE' : '#F3F4F6',
                          color: isCredentialDocument(doc.documentType) ? '#5B21B6' : '#374151',
                        }}>
                          {HR_DOCUMENT_LABELS[doc.documentType] || doc.documentType}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          padding: '2px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 500,
                          ...(() => {
                            const c: Record<string, { backgroundColor: string; color: string }> = {
                              approved: { backgroundColor: '#D1FAE5', color: '#065F46' },
                              submitted: { backgroundColor: '#DBEAFE', color: '#1E40AF' },
                              pending: { backgroundColor: '#FEF3C7', color: '#92400E' },
                              rejected: { backgroundColor: '#FEE2E2', color: '#991B1B' },
                            };
                            return c[doc.status] || c.pending;
                          })()
                        }}>
                          <StatusIcon status={doc.status} />
                          {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
                          backgroundColor: doc.priority === 'high' ? '#FEE2E2' : doc.priority === 'medium' ? '#FEF3C7' : '#F3F4F6',
                          color: doc.priority === 'high' ? '#991B1B' : doc.priority === 'medium' ? '#92400E' : '#374151',
                        }}>
                          {doc.priority}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px', fontSize: '13px', color: '#374151' }}>
                        {doc.dueDate ? new Date(doc.dueDate).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <button
                          onClick={() => { setSelectedDoc(doc); setShowDetailModal(true); setActionNotes(''); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px',
                            backgroundColor: 'transparent', color: '#226F9E', border: '1px solid #226F9E',
                            borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
                          }}
                        >
                          <Eye size={14} /> Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── ALL DOCUMENTS ─────────────────────────────────────────────── */}
      {viewMode === 'documents' && (
        <>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
              <input
                type="text" placeholder="Search documents or employees..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
              />
            </div>
            <select
              value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
              style={{ padding: '10px 16px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <select
              value={filterType} onChange={(e) => setFilterType(e.target.value as FilterType)}
              style={{ padding: '10px 16px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
            >
              <option value="all">All Types</option>
              <option value="credentials">Credentials</option>
              <option value="compliance">Compliance</option>
            </select>
          </div>

          <div style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB' }}>
                  {['Document', 'Employee', 'Type', 'Status', 'Due Date', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.map(doc => (
                  <tr key={doc.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FileText size={16} style={{ color: '#9CA3AF' }} />
                        <span style={{ fontSize: '14px', fontWeight: 500, color: '#0A2540' }}>{doc.formName}</span>
                      </div>
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: '14px', color: '#374151' }}>{doc.employeeName}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
                        backgroundColor: isCredentialDocument(doc.documentType) ? '#EDE9FE' : '#F3F4F6',
                        color: isCredentialDocument(doc.documentType) ? '#5B21B6' : '#374151',
                      }}>
                        {HR_DOCUMENT_LABELS[doc.documentType] || doc.documentType}
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        padding: '2px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 500,
                        ...(() => {
                          const c: Record<string, { backgroundColor: string; color: string }> = {
                            approved: { backgroundColor: '#D1FAE5', color: '#065F46' },
                            submitted: { backgroundColor: '#DBEAFE', color: '#1E40AF' },
                            pending: { backgroundColor: '#FEF3C7', color: '#92400E' },
                            rejected: { backgroundColor: '#FEE2E2', color: '#991B1B' },
                          };
                          return c[doc.status] || c.pending;
                        })()
                      }}>
                        <StatusIcon status={doc.status} />
                        {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: '13px', color: '#374151' }}>
                      {doc.dueDate ? new Date(doc.dueDate).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <button
                        onClick={() => { setSelectedDoc(doc); setShowDetailModal(true); setActionNotes(''); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px',
                          backgroundColor: 'transparent', color: '#226F9E', border: '1px solid #226F9E',
                          borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
                        }}
                      >
                        <Eye size={14} /> View
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredDocuments.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#6B7280' }}>No documents match your filters</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── ASSIGN DOCUMENTS ──────────────────────────────────────────── */}
      {viewMode === 'assign' && (
        <div style={{ backgroundColor: '#FFFFFF', padding: '24px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#0A2540', margin: 0 }}>Assign Documents to Employee</h3>
            <button
              onClick={() => setViewMode('overview')}
              style={{ backgroundColor: 'transparent', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: '14px' }}
            >
              <ChevronLeft size={16} style={{ verticalAlign: 'middle' }} /> Back
            </button>
          </div>

          {/* Employee selector */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>Employee *</label>
            <select
              value={assignEmployeeId}
              onChange={(e) => setAssignEmployeeId(e.target.value)}
              style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
            >
              <option value="">Select an employee...</option>
              {teamMembers.map(m => (
                <option key={m.id} value={m.id}>{m.name} — {m.role}</option>
              ))}
            </select>
          </div>

          {assignEmployeeId && (
            <>
              {/* Quick assign all required */}
              <div style={{ marginBottom: '20px', padding: '12px 16px', backgroundColor: '#F0F9FF', borderRadius: '8px', border: '1px solid #BAE6FD', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ fontWeight: 500, color: '#0A2540', margin: 0, fontSize: '14px' }}>Quick: Assign all required onboarding forms</p>
                  <p style={{ color: '#6B7280', margin: '2px 0 0 0', fontSize: '12px' }}>
                    {DEFAULT_HR_FORM_TEMPLATES.filter(t => t.required).length} required forms (I-9, W-4, NDA, HIPAA Training, etc.)
                  </p>
                </div>
                <button
                  onClick={handleAssignAllRequired}
                  disabled={assigning}
                  style={{
                    padding: '8px 20px', backgroundColor: '#059669', color: '#FFF',
                    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                    opacity: assigning ? 0.6 : 1,
                  }}
                >
                  {assigning ? 'Assigning...' : 'Assign All Required'}
                </button>
              </div>

              {/* Individual form assignment */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>Document *</label>
                  <select
                    value={assignTemplateId}
                    onChange={(e) => setAssignTemplateId(e.target.value)}
                    style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
                  >
                    <option value="">Select a form...</option>
                    <optgroup label="Standard HR Forms">
                      {DEFAULT_HR_FORM_TEMPLATES.map(t => (
                        <option key={t.id} value={t.id}>{t.formName}{t.required ? ' (Required)' : ''}</option>
                      ))}
                    </optgroup>
                    {installedHRTemplates.length > 0 && (
                      <optgroup label="Installed Templates (Fillable)">
                        {installedHRTemplates.map(t => (
                          <option key={`installed:${t.id}`} value={`installed:${t.id}`}>{t.name}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>Priority</label>
                  <select
                    value={assignPriority}
                    onChange={(e) => setAssignPriority(e.target.value as any)}
                    style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>Due Date (optional)</label>
                <input
                  type="date" value={assignDueDate} onChange={(e) => setAssignDueDate(e.target.value)}
                  style={{ padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
                />
              </div>

              <button
                onClick={handleAssign}
                disabled={!assignTemplateId || assigning}
                style={{
                  padding: '10px 24px', backgroundColor: !assignTemplateId ? '#D1D5DB' : '#226F9E', color: '#FFF',
                  border: 'none', borderRadius: '6px', cursor: !assignTemplateId ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: 600,
                }}
              >
                {assigning ? 'Assigning...' : 'Assign Document'}
              </button>

              {assignSuccess && (
                <p style={{ marginTop: '12px', color: '#059669', fontSize: '14px', fontWeight: 500 }}>
                  ✓ {assignSuccess}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* ── LIVE DOCUMENTS VIEW ──────────────────────────────────────── */}
      {viewMode === 'live-docs' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
              Create and edit documents directly in the browser. Changes are saved to Firestore.
            </p>
            <button
              onClick={() => setShowNewDocModal(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px',
                backgroundColor: '#226F9E', color: '#FFFFFF', border: 'none', borderRadius: '8px',
                cursor: 'pointer', fontSize: '14px', fontWeight: 600,
              }}
            >
              <Plus size={16} /> New Live Document
            </button>
          </div>

          {liveDocuments.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '60px', backgroundColor: '#F9FAFB',
              borderRadius: '12px', border: '1px solid #E5E7EB'
            }}>
              <FileText size={48} style={{ color: '#D1D5DB', marginBottom: '12px' }} />
              <p style={{ color: '#6B7280', margin: '0 0 4px 0' }}>No live documents yet</p>
              <p style={{ color: '#9CA3AF', margin: 0, fontSize: '13px' }}>
                Click "New Live Document" to create an editable document.
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
              {liveDocuments.map((doc) => (
                <div
                  key={doc.id}
                  onClick={() => openEditor(doc)}
                  style={{
                    padding: '20px', backgroundColor: '#FFFFFF', borderRadius: '10px',
                    border: '1px solid #E5E7EB', cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#226F9E'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#EFF6FF' }}>
                      <Edit2 size={18} style={{ color: '#226F9E' }} />
                    </div>
                    <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#0A2540' }}>
                      {doc.formName}
                    </h4>
                  </div>
                  <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 8px 0' }}>
                    By {doc.employeeName}
                  </p>
                  {doc.lastEditedAt && (
                    <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>
                      Last edited: {new Date(doc.lastEditedAt).toLocaleString()}
                    </p>
                  )}
                  <div
                    style={{
                      marginTop: '12px', padding: '10px', backgroundColor: '#F9FAFB',
                      borderRadius: '6px', maxHeight: '60px', overflow: 'hidden',
                      fontSize: '12px', color: '#6B7280', lineHeight: '1.4'
                    }}
                    dangerouslySetInnerHTML={{ __html: (doc.liveContent || '').substring(0, 200) || '<em>Empty document</em>' }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── NEW LIVE DOCUMENT MODAL ───────────────────────────────────── */}
      {showNewDocModal && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => setShowNewDocModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#FFFFFF', borderRadius: '12px', padding: '24px',
              width: '440px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#0A2540', margin: '0 0 16px 0' }}>
              New Live Document
            </h3>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '6px' }}>
              Document Title *
            </label>
            <input
              value={newDocTitle}
              onChange={(e) => setNewDocTitle(e.target.value)}
              placeholder="e.g., Office Policy Manual, Staff Handbook"
              style={{
                width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB',
                borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '16px',
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateLiveDoc(); }}
              autoFocus
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => setShowNewDocModal(false)}
                style={{
                  padding: '10px 20px', borderRadius: '8px', border: '1px solid #D1D5DB',
                  backgroundColor: 'transparent', color: '#374151', cursor: 'pointer', fontSize: '14px',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateLiveDoc}
                disabled={!newDocTitle.trim()}
                style={{
                  padding: '10px 24px', borderRadius: '8px', border: 'none',
                  backgroundColor: newDocTitle.trim() ? '#226F9E' : '#D1D5DB',
                  color: '#FFF', cursor: newDocTitle.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '14px', fontWeight: 600,
                }}
              >
                Create & Open Editor
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── LIVE EDITOR MODAL ─────────────────────────────────────────── */}
      {showEditorModal && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => setShowEditorModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#1e293b', borderRadius: '16px', border: '1px solid #334155',
              width: '100%', maxWidth: '800px', maxHeight: '85vh', display: 'flex',
              flexDirection: 'column', overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '16px 24px', borderBottom: '1px solid #334155',
            }}>
              <h3 style={{ margin: 0, color: '#f8fafc', fontSize: '16px' }}>
                {editorTitle}
              </h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={handleSaveContent}
                  disabled={savingContent}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
                    backgroundColor: '#22c55e', color: '#fff', border: 'none', borderRadius: '6px',
                    cursor: savingContent ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600,
                    opacity: savingContent ? 0.7 : 1,
                  }}
                >
                  <Save size={14} /> {savingContent ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setShowEditorModal(false)}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Editor */}
            <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>
              <RichTextEditor
                value={editorContent}
                onChange={setEditorContent}
                placeholder="Start writing your document..."
                minHeight="400px"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── DOCUMENT DETAIL MODAL ─────────────────────────────────────── */}
      {showDetailModal && selectedDoc && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowDetailModal(false)}
        >
          <div
            style={{
              backgroundColor: '#FFFFFF', borderRadius: '12px', padding: '24px', width: '560px', maxHeight: '80vh',
              overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#0A2540', margin: 0 }}>Document Details</h3>
              <button onClick={() => setShowDetailModal(false)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: '#6B7280' }}><X size={20} /></button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              <div>
                <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 2px 0' }}>Document</p>
                <p style={{ fontSize: '14px', fontWeight: 500, color: '#0A2540', margin: 0 }}>{selectedDoc.formName}</p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 2px 0' }}>Employee</p>
                <p style={{ fontSize: '14px', fontWeight: 500, color: '#0A2540', margin: 0 }}>{selectedDoc.employeeName}</p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 2px 0' }}>Status</p>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '2px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 500,
                  ...(() => {
                    const c: Record<string, { backgroundColor: string; color: string }> = {
                      approved: { backgroundColor: '#D1FAE5', color: '#065F46' },
                      submitted: { backgroundColor: '#DBEAFE', color: '#1E40AF' },
                      pending: { backgroundColor: '#FEF3C7', color: '#92400E' },
                      rejected: { backgroundColor: '#FEE2E2', color: '#991B1B' },
                    };
                    return c[selectedDoc.status] || c.pending;
                  })()
                }}>
                  <StatusIcon status={selectedDoc.status} />
                  {selectedDoc.status.charAt(0).toUpperCase() + selectedDoc.status.slice(1)}
                </span>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 2px 0' }}>Priority</p>
                <p style={{ fontSize: '14px', color: '#0A2540', margin: 0 }}>{selectedDoc.priority}</p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 2px 0' }}>Assigned Date</p>
                <p style={{ fontSize: '14px', color: '#0A2540', margin: 0 }}>{new Date(selectedDoc.assignedDate).toLocaleDateString()}</p>
              </div>
              {selectedDoc.dueDate && (
                <div>
                  <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 2px 0' }}>Due Date</p>
                  <p style={{ fontSize: '14px', color: '#0A2540', margin: 0 }}>{new Date(selectedDoc.dueDate).toLocaleDateString()}</p>
                </div>
              )}
              {selectedDoc.expirationDate && (
                <div>
                  <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 2px 0' }}>Expiration</p>
                  <p style={{ fontSize: '14px', color: new Date(selectedDoc.expirationDate) < new Date() ? '#991B1B' : '#0A2540', margin: 0 }}>
                    {new Date(selectedDoc.expirationDate).toLocaleDateString()}
                  </p>
                </div>
              )}
              {selectedDoc.notes && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 2px 0' }}>Notes</p>
                  <p style={{ fontSize: '14px', color: '#0A2540', margin: 0 }}>{selectedDoc.notes}</p>
                </div>
              )}
            </div>

            {/* Document description */}
            {(() => {
              const tmpl = DEFAULT_HR_FORM_TEMPLATES.find(t => t.formName === selectedDoc.formName);
              return tmpl ? (
                <div style={{
                  marginBottom: '16px', padding: '14px 16px', backgroundColor: '#F0F9FF',
                  borderRadius: '8px', border: '1px solid #BAE6FD',
                }}>
                  <p style={{ fontSize: '13px', color: '#1E40AF', margin: 0, lineHeight: '1.5' }}>
                    <strong>About this document:</strong> {tmpl.description}
                  </p>
                  {tmpl.required && (
                    <p style={{ fontSize: '12px', color: '#92400E', margin: '8px 0 0 0', fontWeight: 500 }}>
                      Required document
                    </p>
                  )}
                </div>
              ) : null;
            })()}

            {/* File link */}
            {selectedDoc.fileUrl && (
              <div style={{ marginBottom: '16px' }}>
                <a
                  href={selectedDoc.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
                    backgroundColor: '#F0F9FF', color: '#226F9E', borderRadius: '6px', textDecoration: 'none',
                    fontSize: '13px', fontWeight: 500, border: '1px solid #BAE6FD',
                  }}
                >
                  <Download size={14} /> View Uploaded File
                </a>
              </div>
            )}

            {/* Fill & Sign Form — works for ALL documents by looking up template from library */}
            {(selectedDoc.status === 'pending' || selectedDoc.status === 'rejected') && (
              <div style={{ marginBottom: '16px' }}>
                <button
                  onClick={async () => {
                    // 1. Check installed templates (Firestore) by templateId
                    let tmpl: FormTemplateDefinition | null = null;
                    if (selectedDoc.templateId) {
                      tmpl = installedHRTemplates.find(t => t.id === selectedDoc.templateId) || null;
                      if (!tmpl) {
                        const fetched = await FormTemplateService.getInstance().getTemplatesByCategory('hr');
                        tmpl = fetched.find(t => t.id === selectedDoc.templateId) || null;
                      }
                    }
                    // 2. Fallback: look up by form name in static CLINICAL_FORM_TEMPLATES
                    if (!tmpl) {
                      // Try exact match first, then fuzzy match on normalized words
                      const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, ' ').replace(/\s+/g, ' ').trim();
                      const docWords = normalize(selectedDoc.formName);
                      const staticTmpl = CLINICAL_FORM_TEMPLATES.find(t => t.name === selectedDoc.formName)
                        || CLINICAL_FORM_TEMPLATES.find(t => normalize(t.name) === docWords)
                        || CLINICAL_FORM_TEMPLATES.find(t => t.category === 'hr' && (
                          normalize(t.name).includes(docWords) || docWords.includes(normalize(t.name))
                        ))
                        || CLINICAL_FORM_TEMPLATES.find(t => {
                          if (t.category !== 'hr') return false;
                          const tWords = normalize(t.name).split(' ');
                          const dWords = docWords.split(' ');
                          const matches = tWords.filter(w => dWords.includes(w) && w.length > 2);
                          return matches.length >= 1 && (matches.some(w => w.length > 5));
                        });
                      if (staticTmpl) {
                        tmpl = {
                          id: `static_${staticTmpl.name.replace(/\s+/g, '_').toLowerCase()}`,
                          name: staticTmpl.name,
                          category: staticTmpl.category,
                          description: staticTmpl.description,
                          required: false,
                          elements: staticTmpl.elements,
                          createdAt: new Date().toISOString(),
                          updatedAt: new Date().toISOString(),
                          version: 1,
                        };
                      }
                    }
                    if (!tmpl) {
                      toast.error('Error', 'No form template found for this document');
                      return;
                    }
                    setFormFillTemplate(tmpl);
                    setFormFillDoc(selectedDoc);
                    setFormResponses(selectedDoc.formResponses || {});
                    setSignatureName('');
                    setShowDetailModal(false);
                    setShowFormFillModal(true);
                  }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 20px',
                    backgroundColor: '#059669', color: '#FFF', borderRadius: '6px', border: 'none',
                    fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  <Edit2 size={14} /> Fill & Sign Form
                </button>
              </div>
            )}

            {/* Signed indicator */}
            {selectedDoc.signedAt && (
              <div style={{
                marginBottom: '16px', padding: '12px 16px', backgroundColor: '#D1FAE5', borderRadius: '8px',
                display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#065F46',
              }}>
                <CheckCircle size={16} />
                Signed by {selectedDoc.signedByName} on {new Date(selectedDoc.signedAt).toLocaleDateString()}
              </div>
            )}

            {/* Download PDF (for signed/submitted/approved docs) */}
            {(selectedDoc.signedAt || selectedDoc.status === 'submitted' || selectedDoc.status === 'approved') && (
              <div style={{ marginBottom: '16px' }}>
                <button
                  onClick={async () => {
                    try {
                      const { default: jsPDF } = await import('jspdf');
                      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
                      const pageWidth = pdf.internal.pageSize.getWidth();
                      let y = 20;

                      // Header
                      pdf.setFontSize(18);
                      pdf.setFont('helvetica', 'bold');
                      pdf.text(selectedDoc.formName, pageWidth / 2, y, { align: 'center' });
                      y += 10;

                      pdf.setFontSize(10);
                      pdf.setFont('helvetica', 'normal');
                      pdf.setTextColor(100);
                      pdf.text('My ClinicCore — HR Document', pageWidth / 2, y, { align: 'center' });
                      y += 8;

                      // Divider
                      pdf.setDrawColor(180);
                      pdf.line(20, y, pageWidth - 20, y);
                      y += 10;

                      // Document info
                      pdf.setTextColor(0);
                      pdf.setFontSize(11);
                      const info = [
                        ['Employee:', selectedDoc.employeeName],
                        ['Status:', selectedDoc.status.charAt(0).toUpperCase() + selectedDoc.status.slice(1)],
                        ['Assigned:', new Date(selectedDoc.assignedDate).toLocaleDateString()],
                        ...(selectedDoc.dueDate ? [['Due Date:', new Date(selectedDoc.dueDate).toLocaleDateString()]] : []),
                        ...(selectedDoc.signedAt ? [['Signed:', `${selectedDoc.signedByName} on ${new Date(selectedDoc.signedAt).toLocaleDateString()}`]] : []),
                      ];
                      info.forEach(([label, value]) => {
                        pdf.setFont('helvetica', 'bold');
                        pdf.text(label as string, 20, y);
                        pdf.setFont('helvetica', 'normal');
                        pdf.text(value as string, 65, y);
                        y += 7;
                      });
                      y += 5;

                      // Form responses
                      if (selectedDoc.formResponses && Object.keys(selectedDoc.formResponses).length > 0) {
                        pdf.setDrawColor(180);
                        pdf.line(20, y, pageWidth - 20, y);
                        y += 8;
                        pdf.setFontSize(13);
                        pdf.setFont('helvetica', 'bold');
                        pdf.text('Form Responses', 20, y);
                        y += 8;
                        pdf.setFontSize(10);

                        Object.entries(selectedDoc.formResponses).forEach(([key, val]) => {
                          if (y > 270) { pdf.addPage(); y = 20; }
                          const displayKey = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                          const displayVal = Array.isArray(val) ? val.join(', ') : String(val || '—');
                          pdf.setFont('helvetica', 'bold');
                          pdf.text(displayKey, 20, y);
                          y += 5;
                          pdf.setFont('helvetica', 'normal');
                          const lines = pdf.splitTextToSize(displayVal, pageWidth - 45);
                          pdf.text(lines, 25, y);
                          y += lines.length * 5 + 3;
                        });
                      }

                      // Signature
                      if (selectedDoc.signedByName) {
                        if (y > 250) { pdf.addPage(); y = 20; }
                        y += 5;
                        pdf.setDrawColor(180);
                        pdf.line(20, y, pageWidth - 20, y);
                        y += 10;
                        pdf.setFontSize(12);
                        pdf.setFont('helvetica', 'bold');
                        pdf.text('Signature', 20, y);
                        y += 10;
                        pdf.setFontSize(16);
                        pdf.setFont('times', 'italic');
                        pdf.text(selectedDoc.signedByName, 25, y);
                        y += 8;
                        pdf.setFontSize(9);
                        pdf.setFont('helvetica', 'normal');
                        pdf.setTextColor(100);
                        pdf.text(`Electronically signed on ${new Date(selectedDoc.signedAt!).toLocaleString()}`, 25, y);
                      }

                      // Footer
                      const pageCount = pdf.getNumberOfPages();
                      for (let i = 1; i <= pageCount; i++) {
                        pdf.setPage(i);
                        pdf.setFontSize(8);
                        pdf.setTextColor(150);
                        pdf.text(
                          `Generated ${new Date().toLocaleString()} — Page ${i} of ${pageCount}`,
                          pageWidth / 2, 290, { align: 'center' }
                        );
                      }

                      pdf.save(`${selectedDoc.formName.replace(/\s+/g, '_')}_${selectedDoc.employeeName.replace(/\s+/g, '_')}.pdf`);
                      toast.success('Downloaded', 'PDF saved successfully');
                    } catch (err: any) {
                      toast.error('Error', 'Failed to generate PDF: ' + err.message);
                    }
                  }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 20px',
                    backgroundColor: '#226F9E', color: '#FFF', borderRadius: '6px', border: 'none',
                    fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  <Download size={14} /> Download PDF
                </button>
              </div>
            )}

            {/* Upload file (if pending or rejected) */}
            {(selectedDoc.status === 'pending' || selectedDoc.status === 'rejected') && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
                  backgroundColor: '#226F9E', color: '#FFF', borderRadius: '6px', cursor: 'pointer',
                  fontSize: '13px', fontWeight: 500, opacity: uploading ? 0.6 : 1,
                }}>
                  <Upload size={14} /> {uploading ? 'Uploading...' : 'Upload File'}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileUpload(e, selectedDoc.id)}
                    style={{ display: 'none' }}
                    disabled={uploading}
                  />
                </label>
              </div>
            )}

            {/* Edit Live Content button */}
            {selectedDoc.liveContent !== undefined && (
              <div style={{ marginBottom: '16px' }}>
                <button
                  onClick={() => { setShowDetailModal(false); openEditor(selectedDoc); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
                    backgroundColor: '#226F9E', color: '#FFF', borderRadius: '6px', border: 'none',
                    fontSize: '13px', fontWeight: 500, cursor: 'pointer',
                  }}
                >
                  <Edit2 size={14} /> Edit Document Content
                </button>
              </div>
            )}

            {/* Approve / Reject (if submitted) */}
            {selectedDoc.status === 'submitted' && (
              <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>Review Notes</label>
                  <VoiceInputButton onResult={text => setActionNotes(prev => prev + ' ' + text)} />
                </div>
                <textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  placeholder="Add notes (required for rejection)..."
                  style={{
                    width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px',
                    fontSize: '14px', minHeight: '60px', resize: 'vertical', boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                  <button
                    onClick={() => handleApprove(selectedDoc.id)}
                    style={{
                      flex: 1, padding: '10px', backgroundColor: '#059669', color: '#FFF',
                      border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 600,
                    }}
                  >
                    <CheckCircle size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Approve
                  </button>
                  <button
                    onClick={() => handleReject(selectedDoc.id)}
                    style={{
                      flex: 1, padding: '10px', backgroundColor: '#DC2626', color: '#FFF',
                      border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 600,
                    }}
                  >
                    <XCircle size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Reject
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Form Fill & Sign Modal ───────────────────────────────────────── */}
      {showFormFillModal && formFillDoc && formFillTemplate && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'flex-end',
            zIndex: 1001,
          }}
          onClick={() => setShowFormFillModal(false)}
        >
          <div
            style={{
              width: '600px', maxWidth: '100vw', height: '100vh', overflowY: 'auto',
              backgroundColor: '#0f2a3f', boxShadow: '-4px 0 20px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '20px 24px', borderBottom: '1px solid #28527A',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#e5e7eb', margin: 0 }}>{formFillTemplate.name}</h2>
                <p style={{ fontSize: '13px', color: '#9ca3af', margin: '4px 0 0 0' }}>Fill out and sign below</p>
              </div>
              <button onClick={() => setShowFormFillModal(false)} style={{ padding: '4px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                <X size={22} />
              </button>
            </div>

            {/* Form Fields */}
            <div style={{ padding: '24px' }}>
              {formFillTemplate.elements.map((el, idx) => {
                const key = el.id || `field-${idx}`;
                const val = formResponses[key] || '';

                if (el.type === 'heading') {
                  return <h3 key={key} style={{ fontSize: '16px', fontWeight: 600, color: '#F7E9A0', margin: '20px 0 8px 0', borderBottom: '1px solid #28527A', paddingBottom: '8px' }}>{el.content.title}</h3>;
                }
                if (el.type === 'description') {
                  return <p key={key} style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic', margin: '0 0 12px 0', lineHeight: 1.5 }}>{el.content.text}</p>;
                }
                if (el.type === 'divider') {
                  return <hr key={key} style={{ border: 'none', borderTop: '1px solid #28527A', margin: '16px 0' }} />;
                }
                if (el.type === 'short-response') {
                  return (
                    <div key={key} style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#d1d5db', marginBottom: '4px' }}>
                        {el.content.question} {el.content.required && <span style={{ color: '#ef4444' }}>*</span>}
                      </label>
                      <input
                        type="text" value={val} placeholder={el.content.placeholder || ''}
                        onChange={(e) => setFormResponses({ ...formResponses, [key]: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #28527A', borderRadius: '8px', fontSize: '14px', background: '#1a3a5c', color: '#e5e7eb', boxSizing: 'border-box' }}
                      />
                    </div>
                  );
                }
                if (el.type === 'long-response') {
                  return (
                    <div key={key} style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <label style={{ fontSize: '13px', fontWeight: 500, color: '#d1d5db' }}>
                          {el.content.question} {el.content.required && <span style={{ color: '#ef4444' }}>*</span>}
                        </label>
                        <VoiceInputButton onResult={text => setFormResponses(prev => ({ ...prev, [key]: (prev[key] || '') + ' ' + text }))} />
                      </div>
                      <textarea
                        value={val} placeholder={el.content.placeholder || ''} rows={3}
                        onChange={(e) => setFormResponses({ ...formResponses, [key]: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #28527A', borderRadius: '8px', fontSize: '14px', background: '#1a3a5c', color: '#e5e7eb', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                      />
                    </div>
                  );
                }
                if (el.type === 'date') {
                  return (
                    <div key={key} style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#d1d5db', marginBottom: '4px' }}>
                        {el.content.question} {el.content.required && <span style={{ color: '#ef4444' }}>*</span>}
                      </label>
                      <input
                        type="date" value={val}
                        onChange={(e) => setFormResponses({ ...formResponses, [key]: e.target.value })}
                        style={{ padding: '10px 12px', border: '1px solid #28527A', borderRadius: '8px', fontSize: '14px', background: '#1a3a5c', color: '#e5e7eb' }}
                      />
                    </div>
                  );
                }
                if (el.type === 'single-option') {
                  return (
                    <div key={key} style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#d1d5db', marginBottom: '6px' }}>
                        {el.content.question} {el.content.required && <span style={{ color: '#ef4444' }}>*</span>}
                      </label>
                      {el.content.options?.map((opt, i) => (
                        <label key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', color: '#d1d5db', fontSize: '14px', cursor: 'pointer' }}>
                          <input type="radio" name={key} value={opt} checked={val === opt} onChange={() => setFormResponses({ ...formResponses, [key]: opt })} />
                          {opt}
                        </label>
                      ))}
                    </div>
                  );
                }
                if (el.type === 'multiple-options') {
                  const selected: string[] = val ? (Array.isArray(val) ? val : [val]) : [];
                  return (
                    <div key={key} style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#d1d5db', marginBottom: '6px' }}>
                        {el.content.question} {el.content.required && <span style={{ color: '#ef4444' }}>*</span>}
                      </label>
                      {el.content.options?.map((opt, i) => (
                        <label key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', color: '#d1d5db', fontSize: '14px', cursor: 'pointer' }}>
                          <input type="checkbox" checked={selected.includes(opt)} onChange={(e) => {
                            const newSel = e.target.checked ? [...selected, opt] : selected.filter(s => s !== opt);
                            setFormResponses({ ...formResponses, [key]: newSel });
                          }} />
                          {opt}
                        </label>
                      ))}
                    </div>
                  );
                }
                if (el.type === 'dropdown') {
                  return (
                    <div key={key} style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#d1d5db', marginBottom: '4px' }}>
                        {el.content.question} {el.content.required && <span style={{ color: '#ef4444' }}>*</span>}
                      </label>
                      <select value={val} onChange={(e) => setFormResponses({ ...formResponses, [key]: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #28527A', borderRadius: '8px', fontSize: '14px', background: '#1a3a5c', color: '#e5e7eb' }}>
                        <option value="">Select...</option>
                        {el.content.options?.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                      </select>
                    </div>
                  );
                }
                return null;
              })}

              {/* Signature Section */}
              <div style={{ borderTop: '2px solid #28527A', marginTop: '24px', paddingTop: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#F7E9A0', margin: '0 0 12px 0' }}>Signature</h3>
                <p style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '12px' }}>
                  By typing your full name below, you acknowledge that you have read, understood, and agree to the contents of this document.
                </p>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#d1d5db', marginBottom: '4px' }}>
                  Full Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text" value={signatureName} placeholder="Type your full legal name"
                  onChange={(e) => setSignatureName(e.target.value)}
                  style={{
                    width: '100%', padding: '12px', border: '1px solid #28527A', borderRadius: '8px',
                    fontSize: '16px', fontFamily: "'Georgia', serif", fontStyle: 'italic',
                    background: '#1a3a5c', color: '#e5e7eb', boxSizing: 'border-box',
                  }}
                />
                {signatureName && (
                  <div style={{
                    marginTop: '8px', padding: '12px 16px', backgroundColor: '#1a3a5c', borderRadius: '8px',
                    border: '1px solid #28527A', textAlign: 'center',
                  }}>
                    <span style={{ fontSize: '22px', fontFamily: "'Georgia', serif", fontStyle: 'italic', color: '#6CA6C1' }}>
                      {signatureName}
                    </span>
                    <p style={{ fontSize: '11px', color: '#6b7280', margin: '4px 0 0 0' }}>
                      {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px', borderTop: '1px solid #28527A',
              display: 'flex', justifyContent: 'flex-end', gap: '12px',
              position: 'sticky', bottom: 0, background: '#0f2a3f',
            }}>
              <button
                onClick={() => setShowFormFillModal(false)}
                style={{ padding: '10px 20px', background: 'transparent', color: '#9ca3af', border: '1px solid #28527A', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                disabled={!signatureName.trim()}
                onClick={async () => {
                  if (!formFillDoc || !signatureName.trim()) return;
                  try {
                    const { doc: firestoreDoc, updateDoc: firestoreUpdate } = await import('firebase/firestore');
                    const { db: fireDb } = await import('../../../config/firebase');
                    await firestoreUpdate(firestoreDoc(fireDb, 'hr_documents', formFillDoc.id), {
                      formResponses,
                      signedAt: new Date().toISOString(),
                      signedByName: signatureName.trim(),
                      status: 'submitted',
                      submittedDate: new Date().toISOString(),
                    });
                    toast.success('Form Submitted', `"${formFillTemplate.name}" signed and submitted.`);
                    setShowFormFillModal(false);
                    setFormFillDoc(null);
                    setFormFillTemplate(null);
                  } catch (err: any) {
                    toast.error('Error', 'Failed to submit form: ' + err.message);
                  }
                }}
                style={{
                  padding: '10px 24px',
                  background: signatureName.trim() ? 'linear-gradient(135deg, #10B981, #059669)' : '#374151',
                  color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
                  cursor: signatureName.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                Sign & Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRTab;
